// module_configs/index.js

import { appliancesConfig } from './appliances';
import { autosConfig } from './autos';
import { cosmeticsConfig } from './cosmetics';
import { electronicsConfig } from './electronics';
import { fabricsConfig } from './fabrics';
import { hardwareConfig } from './hardware';
import { mechanicsConfig } from './mechanics';
import { pharmacyConfig } from './pharmacy';
import { posConfig } from './pos';
import { pvcConfig } from './pvc';
import { salantyConfig } from './salanty';
import { solarConfig } from './solar';
import { superadminConfig } from './superadmin';

// ==============================================
// MODULE REGISTRY
// ==============================================
export const MODULE_CONFIGS = {
  appliances: appliancesConfig,
  autos: autosConfig,
  cosmetics: cosmeticsConfig,
  electronics: electronicsConfig,
  fabrics: fabricsConfig,
  hardware: hardwareConfig,
  mechanics: mechanicsConfig,
  pharmacy: pharmacyConfig,
  pos: posConfig,
  pvc: pvcConfig,
  salanty: salantyConfig,
  solar: solarConfig,
  superadmin: superadminConfig
};

// ==============================================
// ROLE TO MODULE ID MAPPING
// ==============================================
export const ROLE_TO_MODULE = {
  // Super Admin
  'super_admin': 'superadmin',
  'superadmin': 'superadmin',

  // Appliances
  'admin_appliances': 'appliances',
  'manager_appliances': 'appliances',
  'user_appliances': 'appliances',
  'viewer_appliances': 'appliances',

  // Autos - Thermal
  'admin_autosth': 'autos',
  'manager_autosth': 'autos',
  'user_autosth': 'autos',
  'viewer_autosth': 'autos',
  // Autos - Large
  'admin_autosap': 'autos',
  'manager_autosap': 'autos',
  'user_autosap': 'autos',
  'viewer_autosap': 'autos',

  // Cosmetics
  'admin_cosmetics': 'cosmetics',
  'manager_cosmetics': 'cosmetics',
  'user_cosmetics': 'cosmetics',
  'viewer_cosmetics': 'cosmetics',

  // Electronics
  'admin_electronics': 'electronics',
  'manager_electronics': 'electronics',
  'user_electronics': 'electronics',
  'viewer_electronics': 'electronics',

  // Fabrics
  'admin_fabrics': 'fabrics',
  'manager_fabrics': 'fabrics',
  'user_fabrics': 'fabrics',
  'viewer_fabrics': 'fabrics',

  // Hardware - Thermal
  'admin_hardwareth': 'hardware',
  'manager_hardwareth': 'hardware',
  'user_hardwareth': 'hardware',
  'viewer_hardwareth': 'hardware',
  // Hardware - Large
  'admin_hardwareap': 'hardware',
  'manager_hardwareap': 'hardware',
  'user_hardwareap': 'hardware',
  'viewer_hardwareap': 'hardware',

  // Mechanics - Thermal
  'admin_mechanicsth': 'mechanics',
  'manager_mechanicsth': 'mechanics',
  'user_mechanicsth': 'mechanics',
  'viewer_mechanicsth': 'mechanics',
  // Mechanics - Large
  'admin_mechanicsap': 'mechanics',
  'manager_mechanicsap': 'mechanics',
  'user_mechanicsap': 'mechanics',
  'viewer_mechanicsap': 'mechanics',

  // Pharmacy
  'admin_pharmacy': 'pharmacy',
  'manager_pharmacy': 'pharmacy',
  'user_pharmacy': 'pharmacy',
  'viewer_pharmacy': 'pharmacy',
  // Pharmacy TAX
  'admin_pharmacytax': 'pharmacy',
  'manager_pharmacytax': 'pharmacy',
  'user_pharmacytax': 'pharmacy',
  'viewer_pharmacytax': 'pharmacy',

  // POS
  'admin_pos': 'pos',
  'manager_pos': 'pos',
  'user_pos': 'pos',
  'viewer_pos': 'pos',
  'salesman_pos': 'pos',

  // PO
  'admin_po': 'pos',
  'manager_po': 'pos',
  'user_po': 'pos',
  'viewer_po': 'pos',

  // PVC
  'admin_pvc': 'pvc',
  'manager_pvc': 'pvc',
  'user_pvc': 'pvc',
  'viewer_pvc': 'pvc',

  // Salanty
  'admin_salanty': 'salanty',
  'manager_salanty': 'salanty',
  'user_salanty': 'salanty',
  'viewer_salanty': 'salanty',

  // Solar
  'admin_solar': 'solar',
  'manager_solar': 'solar',
  'user_solar': 'solar',
  'viewer_solar': 'solar'
};

// ==============================================
// HELPER FUNCTIONS
// ==============================================

/**
 * Get module configuration by user role
 * @param {string} role - User role (e.g., 'admin_cosmetics')
 * @returns {object|null} - Module configuration object or null if not found
 */
export const getModuleByRole = (role) => {
  if (!role) return null;
  
  const moduleId = ROLE_TO_MODULE[role];
  if (!moduleId) {
    console.warn(`No module found for role: ${role}`);
    return null;
  }
  
  const moduleConfig = MODULE_CONFIGS[moduleId];
  if (!moduleConfig) {
    console.warn(`Module config not found for module: ${moduleId}`);
    return null;
  }
  
  return moduleConfig;
};

/**
 * Get module configuration by module ID
 * @param {string} moduleId - Module ID (e.g., 'cosmetics')
 * @returns {object|null} - Module configuration object or null if not found
 */
export const getModuleConfig = (moduleId) => {
  if (!moduleId) return null;
  return MODULE_CONFIGS[moduleId] || null;
};

/**
 * Get all available module IDs
 * @returns {string[]} - Array of module IDs
 */
export const getAllModules = () => {
  return Object.keys(MODULE_CONFIGS);
};

/**
 * Check if a role exists in the role-to-module mapping
 * @param {string} role - User role
 * @returns {boolean} - True if role exists
 */
export const isValidRole = (role) => {
  return role && ROLE_TO_MODULE.hasOwnProperty(role);
};

/**
 * Get module name by role
 * @param {string} role - User role
 * @returns {string|null} - Module name or null if not found
 */
export const getModuleNameByRole = (role) => {
  const config = getModuleByRole(role);
  return config?.moduleName || null;
};

/**
 * Get dashboard component by role
 * @param {string} role - User role
 * @returns {component|null} - Dashboard component or null if not found
 */
export const getDashboardComponentByRole = (role) => {
  const config = getModuleByRole(role);
  return config?.dashboardComponent || null;
};

/**
 * Get menus by role
 * @param {string} role - User role
 * @returns {array} - Array of menus or empty array if not found
 */
export const getMenusByRole = (role) => {
  const config = getModuleByRole(role);
  return config?.menus || [];
};